const ACCOUNT_LOGIN_REDIRECT_SELECTOR = '[data-account-login-redirect]';
const FALLBACK_LINK_SELECTOR = '[data-account-login-fallback-link]';
const MANAGED_LOGIN_PATH_PREFIX = '/customer_authentication/login';
// MyHobbii's hash router only ever produces paths like #/orders or
// #/stash/patterns, optionally with a query-like suffix. Restricting to that
// shape before it's folded into the managed-login return_to keeps arbitrary
// hash content (e.g. a crafted `#&return_to=`) from riding along.
const SAFE_HASH_ROUTE_PATTERN = /^#\/[\w-]*(?:\/[\w-]*)*(?:\?[\w.=&%-]*)?$/;
const REDIRECT_ATTEMPTED_KEY = 'account_login_redirect_attempted';

// sessionStorage can throw (privacy mode, disabled storage, sandboxed
// iframes). This runs at module load in the site-wide entrypoint, so an
// uncaught throw here would take the rest of that module's setup down with
// it -- fall back to "no guard available" instead.
const hasAttemptedRedirect = (): boolean => {
  try {
    return Boolean(sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY));
  } catch {
    return false;
  }
};

const markRedirectAttempted = (): void => {
  try {
    sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, '1');
  } catch {
    // No guard available -- worst case this can loop, same as before this
    // guard existed.
  }
};

// Preserves any hash sub-route (e.g. #/orders) so the customer lands back on
// the page they requested, by folding it into the managed-login return_to.
// Only applies to the managed-login URL -- the classic fallback takes no
// return_to, so appending there would corrupt the classic login path.
const buildLoginTarget = (loginUrl: string): string => {
  if (
    loginUrl.startsWith(MANAGED_LOGIN_PATH_PREFIX) &&
    SAFE_HASH_ROUTE_PATTERN.test(window.location.hash)
  ) {
    return loginUrl + encodeURIComponent(window.location.hash);
  }
  return loginUrl;
};

// Guest visitors to the account page are handed off to login (classic or
// managed, see managed-account-login-url.liquid). If the Cognito round-trip
// lands back here still logged out -- most likely the Shopify session cookie
// hasn't propagated yet -- redirecting again would loop forever, so this
// only ever redirects once per browser session and surfaces a manual link.
export const initAccountLoginRedirect = (): void => {
  const container = document.querySelector<HTMLElement>(
    ACCOUNT_LOGIN_REDIRECT_SELECTOR
  );
  const loginUrl = container?.dataset.loginUrl;
  if (!container || !loginUrl) return;

  const target = buildLoginTarget(loginUrl);

  if (hasAttemptedRedirect()) {
    const fallbackLink = container.querySelector<HTMLAnchorElement>(
      FALLBACK_LINK_SELECTOR
    );
    if (fallbackLink) {
      fallbackLink.href = target;
      fallbackLink.removeAttribute('hidden');
    }
    return;
  }

  markRedirectAttempted();
  window.location.href = target;
};

// Called once the account SPA boots for a real customer, so a later
// logout-then-login cycle in the same tab isn't blocked by a stale guard.
export const clearAccountLoginRedirectGuard = (): void => {
  try {
    sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
  } catch {
    // Nothing to clear if storage isn't available in the first place.
  }
};
